import { useMemo, useState, type FormEvent } from 'react';
import { useOp } from '../api.ts';
import { Link, useRouter } from '../router.tsx';
import { runWrite, useChanges } from '../ui/changes.ts';
import { step, useKeys } from '../ui/keys.ts';
import { Drawer, ErrorBox, Kbd, Loading } from '../ui/kit.tsx';
import { useToast } from '../ui/toasts.tsx';
import { OrgDrawer } from './tenancy/OrgDrawer.tsx';
import { SiteDrawer } from './tenancy/SiteDrawer.tsx';
import { CLICKOUT_LABELS, TYPE_LABELS, type OverviewBrand, type OverviewOrganization, type SiteOptions, type TenancyOverview } from './tenancy/types.ts';

/** Organizations → brands → sites. Rows open the site drawer; admins add and change organizations, brands and sites here. */
export function Sites() {
  const { query, setQuery } = useRouter();
  const changes = useChanges();
  const overview = useOp<TenancyOverview>('admin.tenancy.overview', {}, changes);
  const [cursor, setCursor] = useState(0);
  const [orgDrawer, setOrgDrawer] = useState<string | null>(null);
  const [newSiteFor, setNewSiteFor] = useState<OverviewBrand | null>(null);
  const [newOrg, setNewOrg] = useState(false);
  const siteRef = query.get('site');

  const rows = useMemo(() => overview.data?.organizations.flatMap((o) => o.brands.flatMap((b) => b.sites)) ?? [], [overview.data]);
  useKeys(
    {
      j: () => setCursor((c) => step(c, 1, rows.length)),
      k: () => setCursor((c) => step(c, -1, rows.length)),
      enter: () => rows[cursor] && setQuery({ site: rows[cursor]!.ref }),
      o: () => rows[cursor] && setQuery({ site: rows[cursor]!.ref }),
    },
    !siteRef && !orgDrawer && !newSiteFor && !newOrg,
  );

  const org = overview.data?.organizations.find((o) => o.ref === orgDrawer) ?? null;
  return (
    <section className="page">
      <header className="page-head">
        <div>
          <h1>Sites</h1>
          <p className="muted">Organizations, brands and their sites. Open a site for its settings, addresses and placements.</p>
        </div>
        {overview.data?.canCreateOrganization ? (
          <button type="button" className="btn" onClick={() => setNewOrg(true)}>
            New organization
          </button>
        ) : null}
      </header>
      {overview.error ? <ErrorBox error={overview.error} onRetry={overview.reload} /> : null}
      {!overview.data && overview.loading ? <Loading /> : null}
      {overview.data?.organizations.map((organization) => (
        <OrganizationCard
          key={organization.ref}
          org={organization}
          cursorRef={rows[cursor]?.ref}
          onOpenSite={(ref) => setQuery({ site: ref })}
          onSettings={() => setOrgDrawer(organization.ref)}
          onNewSite={setNewSiteFor}
        />
      ))}
      {siteRef ? <SiteDrawer siteRef={siteRef} onClose={() => setQuery({ site: null })} /> : null}
      {org ? <OrgDrawer org={org} onClose={() => setOrgDrawer(null)} /> : null}
      {newSiteFor ? <NewSiteDrawer brand={newSiteFor} onClose={() => setNewSiteFor(null)} onCreated={(ref) => { setNewSiteFor(null); setQuery({ site: ref }); }} /> : null}
      {newOrg ? <NewOrganizationDrawer onClose={() => setNewOrg(false)} /> : null}
    </section>
  );
}

function aiSummary(org: OverviewOrganization): string {
  if (org.noAiContent) return 'No AI content';
  if (!org.aiCeiling) return 'AI: no ceiling';
  const overrides = Object.keys(org.aiCeiling.capabilities).length;
  return `AI ceiling: ${org.aiCeiling.default ?? 'auto'}${overrides ? ` (+${overrides} specific)` : ''}`;
}

function OrganizationCard({
  org,
  cursorRef,
  onOpenSite,
  onSettings,
  onNewSite,
}: {
  org: OverviewOrganization;
  cursorRef: string | undefined;
  onOpenSite: (ref: string) => void;
  onSettings: () => void;
  onNewSite: (brand: OverviewBrand) => void;
}) {
  const toast = useToast();
  const [addingBrand, setAddingBrand] = useState(false);
  const [brandName, setBrandName] = useState('');
  const [renaming, setRenaming] = useState<string | null>(null);
  const [rename, setRename] = useState('');

  const addBrand = async (event: FormEvent) => {
    event.preventDefault();
    const slug = brandName.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    if (await runWrite(toast, 'admin.brands.create', { organizationRef: org.ref, name: brandName, slug }, `Brand ${brandName} added`)) {
      setBrandName('');
      setAddingBrand(false);
    }
  };
  const saveRename = async (event: FormEvent, brand: OverviewBrand) => {
    event.preventDefault();
    if (await runWrite(toast, 'admin.brands.update', { ref: brand.ref, name: rename }, 'Brand renamed')) setRenaming(null);
  };

  return (
    <article className="card org" aria-labelledby={`org-${org.ref}`}>
      <header className="org-head">
        <h2 id={`org-${org.ref}`}>{org.name}</h2>
        {org.isOperator ? <span className="badge badge-review">Platform</span> : null}
        {org.status !== 'active' ? <span className="badge">inactive</span> : null}
        <span className="muted small">{aiSummary(org)}</span>
        <span className="org-actions">
          <Link className="btn btn-small btn-ghost" to={`/themes?level=organization&ref=${org.ref}`}>
            Theme
          </Link>
          <button type="button" className="btn btn-small" onClick={onSettings}>
            {org.canAdmin ? 'Settings' : 'Details'}
          </button>
          {org.canAdmin ? (
            <button type="button" className="btn btn-small" onClick={() => setAddingBrand(true)}>
              Add brand
            </button>
          ) : null}
        </span>
      </header>
      {addingBrand ? (
        <form className="inline-form" onSubmit={addBrand} aria-label="New brand">
          <label>
            Brand name
            <input autoFocus required minLength={2} value={brandName} onChange={(e) => setBrandName(e.target.value)} />
          </label>
          <button type="submit" className="btn btn-primary">
            Add brand
          </button>
          <button type="button" className="btn btn-ghost" onClick={() => setAddingBrand(false)}>
            Cancel
          </button>
        </form>
      ) : null}
      {org.brands.length === 0 ? <p className="muted">No brands yet.</p> : null}
      {org.brands.map((brand) => (
        <div key={brand.ref} className="brand-block">
          <div className="brand-head">
            {renaming === brand.ref ? (
              <form className="inline-form" onSubmit={(e) => saveRename(e, brand)} aria-label="Rename brand">
                <input autoFocus aria-label="Brand name" value={rename} onChange={(e) => setRename(e.target.value)} />
                <button type="submit" className="btn btn-small btn-primary">
                  Save
                </button>
                <button type="button" className="btn btn-small btn-ghost" onClick={() => setRenaming(null)}>
                  Cancel
                </button>
              </form>
            ) : (
              <h3>{brand.name}</h3>
            )}
            <span className="org-actions">
              <Link className="btn btn-small btn-ghost" to={`/themes?level=brand&ref=${brand.ref}`}>
                Theme
              </Link>
              {brand.canAdmin && renaming !== brand.ref ? (
                <>
                  <button type="button" className="btn btn-small btn-ghost" onClick={() => { setRenaming(brand.ref); setRename(brand.name); }}>
                    Rename
                  </button>
                  <button type="button" className="btn btn-small" onClick={() => onNewSite(brand)}>
                    Add site
                  </button>
                </>
              ) : null}
            </span>
          </div>
          {brand.sites.length ? (
            <table className="table compact">
              <thead>
                <tr>
                  <th scope="col">Site</th>
                  <th scope="col">Address</th>
                  <th scope="col">Type</th>
                  <th scope="col">Status</th>
                  <th scope="col">Your role</th>
                </tr>
              </thead>
              <tbody>
                {brand.sites.map((site) => (
                  <tr key={site.ref} className={`clickable ${site.ref === cursorRef ? 'cursor' : ''}`} onClick={() => onOpenSite(site.ref)}>
                    <th scope="row">
                      <a href={`?site=${site.ref}`} onClick={(e) => e.preventDefault()} className="strong">
                        {site.name}
                      </a>
                      <small className="block muted">{site.market}</small>
                    </th>
                    <td>
                      <code>
                        {site.hostname}
                        {site.basePath !== '/' ? site.basePath : ''}
                      </code>
                    </td>
                    <td>{TYPE_LABELS[site.type] ?? site.type}</td>
                    <td>{site.status === 'active' ? 'Active' : <span className="badge">inactive</span>}</td>
                    <td>{site.role}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="muted small">No sites yet.</p>
          )}
        </div>
      ))}
    </article>
  );
}

function NewSiteDrawer({ brand, onClose, onCreated }: { brand: OverviewBrand; onClose: () => void; onCreated: (ref: string) => void }) {
  const toast = useToast();
  const options = useOp<SiteOptions>('admin.sites.options');
  const [name, setName] = useState(`${brand.name} `);
  const [type, setType] = useState('standalone_domain');
  const [marketCode, setMarketCode] = useState('GB');
  const [hostname, setHostname] = useState('');
  const [basePath, setBasePath] = useState('/');
  const [clickoutMode, setClickoutMode] = useState('new_tab_modal');

  const create = async (event: FormEvent) => {
    event.preventDefault();
    const result = await runWrite<{ ref: string }>(toast, 'admin.sites.create', { brandRef: brand.ref, name, type, marketCode, hostname, basePath, clickoutMode }, `Site ${name} created (noindex until it has its own content)`);
    if (result) onCreated(result.ref);
  };

  return (
    <Drawer title={`New site for ${brand.name}`} onClose={onClose}>
      {!options.data ? (
        <Loading />
      ) : (
        <form onSubmit={create} aria-label="New site">
          <div className="form-grid">
            <label className="span-2">
              Internal name
              <input autoFocus required minLength={2} value={name} onChange={(e) => setName(e.target.value)} />
            </label>
            <label>
              Type
              <select value={type} onChange={(e) => setType(e.target.value)}>
                {options.data.types.map((t) => (
                  <option key={t} value={t}>
                    {TYPE_LABELS[t] ?? t}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Market
              <select value={marketCode} onChange={(e) => setMarketCode(e.target.value)}>
                {options.data.markets.map((m) => (
                  <option key={m.code} value={m.code}>
                    {m.name} ({m.currency})
                  </option>
                ))}
              </select>
            </label>
            <label>
              Host name
              <input required value={hostname} placeholder={type === 'publisher_subfolder' ? 'www.publisher.com' : 'www.example.com'} onChange={(e) => setHostname(e.target.value)} />
            </label>
            <label>
              Base path
              <input value={basePath} placeholder={type === 'publisher_subfolder' ? '/vouchercodes' : '/'} onChange={(e) => setBasePath(e.target.value)} />
            </label>
            <label className="span-2">
              Clickout
              <select value={clickoutMode} onChange={(e) => setClickoutMode(e.target.value)}>
                {options.data.clickoutModes.map((m) => (
                  <option key={m} value={m}>
                    {CLICKOUT_LABELS[m] ?? m}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <p className="muted small">
            {type === 'embed_only' || type === 'api_only'
              ? 'Embed and API sites have no pages; the host name is where the module or partner runs.'
              : 'New sites start as noindex. Turn indexing on in the site settings once it has its own texts.'}
          </p>
          <div className="form-actions">
            <button type="submit" className="btn btn-primary">
              Create site
            </button>
            <span className="muted small">
              <Kbd>Esc</Kbd> cancels
            </span>
          </div>
        </form>
      )}
    </Drawer>
  );
}

function NewOrganizationDrawer({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [noAi, setNoAi] = useState(false);
  const slug = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const create = async (event: FormEvent) => {
    event.preventDefault();
    if (await runWrite(toast, 'admin.orgs.create', { name, slug, noAiContent: noAi }, `Organization ${name} created; you are its owner`)) onClose();
  };
  return (
    <Drawer title="New organization" onClose={onClose}>
      <form onSubmit={create} aria-label="New organization">
        <div className="form-grid">
          <label className="span-2">
            Name
            <input autoFocus required minLength={2} value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <p className="muted small span-2">Slug: {slug || '—'}</p>
          <label className="inline-check span-2">
            <input type="checkbox" checked={noAi} onChange={(e) => setNoAi(e.target.checked)} /> No AI content for this organization
          </label>
        </div>
        <button type="submit" className="btn btn-primary">
          Create organization
        </button>
      </form>
    </Drawer>
  );
}
